import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Play,
  ExternalLink,
  FileCode,
  Globe,
} from "lucide-react";
import { runSitemapCheck, type CheckReport } from "@/lib/sitemap-check.functions";

export const Route = createFileRoute("/sitemap-check")({
  head: () => ({
    meta: [
      { title: "Verificador de Sitemap — MEI Toolbox" },
      {
        name: "description",
        content: "Verifica se cada URL do sitemap responde 200 e se o XML está bem formado.",
      },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: SitemapCheckPage,
});

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        ok
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
          : "bg-red-500/15 text-red-700 dark:text-red-400"
      }`}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function SitemapCheckPage() {
  const run = useServerFn(runSitemapCheck);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<CheckReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const r = await run();
      setReport(r);
    } catch (e) {
      setError((e as Error).message || "Erro ao executar verificação");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-hero opacity-95" />
        <div className="relative container mx-auto px-4 py-14 md:py-16 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
            <Globe className="h-3.5 w-3.5" />
            Painel interno
          </div>
          <h1 className="mt-5 font-display text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl text-balance">
            Verificador de Sitemap
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl">
            Testa o sitemap index, valida o XML de cada sub-sitemap e confere o status HTTP de todas
            as URLs publicadas.
          </p>
          <button
            onClick={start}
            disabled={loading}
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-primary shadow-soft hover:opacity-90 transition disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Verificando...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" /> Executar verificação
              </>
            )}
          </button>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10 max-w-4xl space-y-8">
        {error && (
          <div className="rounded-2xl border border-red-300 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-800 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!report && !loading && !error && (
          <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground">
            <FileCode className="h-8 w-8 mx-auto mb-3 text-primary" />
            Clique em <strong>Executar verificação</strong> para começar.
            <br />
            <span className="text-xs">
              A verificação pode levar alguns segundos dependendo do número de URLs.
            </span>
          </div>
        )}

        {report && (
          <>
            {/* Resumo */}
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Total de URLs
                </div>
                <div className="mt-1 text-2xl font-bold text-foreground">{report.totals.urls}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  OK (200)
                </div>
                <div className="mt-1 text-2xl font-bold text-emerald-600">{report.totals.ok}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Falhas</div>
                <div className="mt-1 text-2xl font-bold text-red-600">{report.totals.failed}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Duração
                </div>
                <div className="mt-1 text-2xl font-bold text-foreground">
                  {(report.durationMs / 1000).toFixed(1)}s
                </div>
              </div>
            </div>

            {/* Index */}
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Sitemap index
                  </div>
                  <a
                    href={report.index.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 font-mono text-sm text-foreground hover:underline break-all"
                  >
                    {report.index.url} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusPill ok={report.index.ok} label={`HTTP ${report.index.status ?? "—"}`} />
                  <StatusPill
                    ok={report.index.xmlValid}
                    label={report.index.xmlValid ? "XML válido" : "XML inválido"}
                  />
                </div>
              </div>
              {report.index.xmlError && (
                <div className="mt-3 text-xs text-red-600">{report.index.xmlError}</div>
              )}
              <div className="mt-3 text-xs text-muted-foreground">
                {report.index.children.length} sub-sitemap(s) referenciado(s)
              </div>
            </div>

            {/* Sub-sitemaps */}
            {report.sitemaps.map((s) => {
              const failed = s.urls.filter((u) => !u.ok);
              return (
                <div key={s.url} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Sub-sitemap
                      </div>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 font-mono text-sm text-foreground hover:underline break-all"
                      >
                        {s.url} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <StatusPill ok={s.ok} label={`HTTP ${s.status ?? "—"}`} />
                      <StatusPill
                        ok={s.xmlValid}
                        label={s.xmlValid ? "XML válido" : "XML inválido"}
                      />
                      <StatusPill
                        ok={failed.length === 0}
                        label={`${s.urlCount - failed.length}/${s.urlCount} URLs OK`}
                      />
                    </div>
                  </div>
                  {s.xmlError && <div className="mt-3 text-xs text-red-600">{s.xmlError}</div>}

                  {failed.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-2">
                        URLs com falha ({failed.length})
                      </div>
                      <ul className="space-y-1.5">
                        {failed.map((u) => (
                          <li
                            key={u.url}
                            className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50/60 dark:bg-red-950/20 px-3 py-2 text-xs"
                          >
                            <a
                              href={u.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-foreground hover:underline break-all"
                            >
                              {u.url}
                            </a>
                            <span className="shrink-0 font-semibold text-red-700 dark:text-red-400">
                              {u.status ?? u.error ?? "erro"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <details className="mt-4">
                    <summary className="text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground">
                      Ver todas as {s.urlCount} URLs
                    </summary>
                    <ul className="mt-2 space-y-1 max-h-72 overflow-auto pr-2">
                      {s.urls.map((u) => (
                        <li
                          key={u.url}
                          className="flex items-center justify-between gap-3 text-xs py-1 border-b border-border/50"
                        >
                          <span className="font-mono truncate text-foreground">{u.url}</span>
                          <span
                            className={`shrink-0 font-semibold ${
                              u.ok ? "text-emerald-600" : "text-red-600"
                            }`}
                          >
                            {u.status ?? u.error ?? "—"} · {u.ms}ms
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              );
            })}
          </>
        )}
      </section>
    </>
  );
}
